import { Inject, Injectable } from '@angular/core';
import { CookieService } from 'ngx-cookie-service';
import { BehaviorSubject, catchError, combineLatest, combineLatestAll, filter, firstValueFrom, map, merge, throwError, timeout } from 'rxjs';
import * as CryptoJS from 'crypto-js';
import { LOCAL_STORAGE } from '@ng-web-apis/common';
import SecurityUtils from '../shared/Utility/securityUtils';
import { PasswordType, UserProfileService } from './user-profile.service';
import { NetworkConnectionService } from './network-connection.service';

@Injectable({
  providedIn: 'root',
})
export class ClientEncryptionPasswordService {
  private customKEKSource = new BehaviorSubject<string>(null);
  public customKEK$ = this.customKEKSource.asObservable();
  // ===========================================================================================
  // The Key Encryption Key KEK is retrieved from the server upon login from the Users Profile
  // It is stored in this class for use to encrypt and decrypt the Content Encryption Key (CEK)
  // If the Application is Offline and app has refreshed, then notes can't be decrypted
  // until app goes back online.
  // Under most situations, the users won't face this issue.
  // ===========================================================================================

  private KEKSource = new BehaviorSubject<string>(null);
  public KEK$ = this.KEKSource.asObservable();
  // =============================================================
  //
  // =============================================================

  constructor(
    @Inject(LOCAL_STORAGE) private storage: Storage,
    private securityUtils: SecurityUtils,
    private userProfileService: UserProfileService,
    private networkConnectionService: NetworkConnectionService
  ) {
    // Constructor
  }

  getKEKObs() {
    if (this.checkUseCustomKEK()) {
      if (this.KEKSource.value) {
        return this.KEK$;
      }
      return this.customKEK$;
    }
    if (this.securityUtils.isServiceWorkerCacheEnabled() && this.userProfileService.isCachedUser()) {
      return merge(this.KEK$, this.customKEK$).pipe(
        filter((e) => !!e),
      );
    }
    return this.KEK$;
  }

  checkUseCustomKEK() {
    return !this.networkConnectionService.isOnline
      && this.securityUtils.isServiceWorkerCacheEnabled()
      && this.userProfileService.isCachedUser()
  }

  getKEKPromise() {
    return firstValueFrom(this.getKEKObs().pipe(filter((e) => !!e)));
  }

  getKEK() {
    return this.KEKSource.value || this.customKEKSource.value;
  }

  // =============================================================
  //
  // =============================================================
  setUserKEK(contentEncryptionKey: string) {
    // Removed: KEK must never be logged. SECURITY-AUDIT-2026-07.md (CR-1).
    this.KEKSource.next(contentEncryptionKey);
  }

  getUserKEK() {
    return this.KEKSource.value;
  }

  // =============================================================
  //
  // =============================================================
  getPassword() {
    return this.getKEK();
    // if (this.KEKSource.value) {
    //   console.log('INDEX-DB --> KEK Set = ', this.KEKSource.value);
    //   return this.KEKSource.value;
    // }
  }

  // =============================================================
  //
  // =============================================================
  clearUserKEK() {
    this.KEKSource.next(null);
  }

  // ===========================================================================
  // Offline unlock — v2 scheme (.planning/UNLOCK-KEY-MIGRATION.md).
  //
  // v2 envelopes carry a per-user random salt + PBKDF2 iteration count; the
  // unlock key is PBKDF2-SHA256(password, salt, iterations). v1 envelopes
  // (no version field) were keyed on unsalted single-pass SHA-256 — they still
  // unlock via the legacy hash, then are SILENTLY re-cached as v2 while the
  // password is in scope. No user action, no prompts.
  // ===========================================================================
  async setCustomKEK(password: string): Promise<boolean> {
    const isCachedUser = this.userProfileService.isCachedUser();
    if (!isCachedUser) {
      return false;
    }

    const meta = this.userProfileService.getCachedEnvelopeMeta();

    let user: any = null;
    let needsUpgrade = false;

    if (meta && meta.v >= 2 && meta.kdfSalt) {
      const unlockKey = await this.deriveUnlockKey(password, meta.kdfSalt, meta.kdfIterations);
      user = await this.userProfileService.loadUserProfileFromCache(unlockKey);
    } else {
      // Legacy (v1) envelope — unsalted SHA-256 key.
      const legacyHash = this.legacyHashPassword(password);
      user = await this.userProfileService.loadUserProfileFromCache(legacyHash);
      needsUpgrade = !!user;
    }

    if (user) {
      if (!this.userProfileService.user.userID) {
        this.userProfileService.setUserProfile(user);
      }
      this.customKEKSource.next(user.clientEncryptionKEK);

      if (needsUpgrade) {
        // Transparent v1 -> v2 upgrade: fresh salt, strong KDF, FN3 envelope.
        // Failure here is non-fatal — the unlock already succeeded and the v1
        // blob remains readable; the upgrade retries on the next unlock.
        try {
          const type = (meta && meta.type) || this.userProfileService.getPasswordType() || 'Password';
          await this.setupCustomKEK(password, type);
        } catch (ex) {
          console.error('Unlock-envelope upgrade failed (will retry next unlock)');
        }
      }
      return true;
    }
    return false;
  }

  async setupCustomKEK(password: string, type: PasswordType): Promise<void> {
    const saltBytes = crypto.getRandomValues(new Uint8Array(16));
    let saltB64 = '';
    saltBytes.forEach((b) => saltB64 += String.fromCharCode(b));
    saltB64 = btoa(saltB64);

    const unlockKey = await this.deriveUnlockKey(password, saltB64, ClientEncryptionPasswordService.UNLOCK_KDF_ITERATIONS);
    const clientEncryptionKEK = await this.userProfileService.cacheUserProfile(
      unlockKey, type, saltB64, ClientEncryptionPasswordService.UNLOCK_KDF_ITERATIONS);
    this.customKEKSource.next(clientEncryptionKEK);
  }

  // ===========================================================================
  // PBKDF2-SHA256 for the HUMAN-CHOSEN unlock secret. 600k iterations (OWASP
  // guidance) — runs once per unlock/setup, ~150-400ms. This is deliberately
  // heavier than PGPEncryptionService's internal KDF, which handles
  // machine-generated keys only.
  // ===========================================================================
  private static readonly UNLOCK_KDF_ITERATIONS = 600000;

  private async deriveUnlockKey(password: string, saltB64: string, iterations?: number): Promise<string> {
    const saltBinary = atob(saltB64);
    const salt = new Uint8Array(saltBinary.length);
    for (let i = 0; i < saltBinary.length; i++) {
      salt[i] = saltBinary.charCodeAt(i);
    }

    const baseKey = await crypto.subtle.importKey(
      'raw', new TextEncoder().encode(password), 'PBKDF2', false, ['deriveBits']);

    const bits = await crypto.subtle.deriveBits(
      {
        name: 'PBKDF2',
        salt,
        iterations: iterations || ClientEncryptionPasswordService.UNLOCK_KDF_ITERATIONS,
        hash: 'SHA-256',
      },
      baseKey,
      256
    );

    return Array.from(new Uint8Array(bits)).map((b) => b.toString(16).padStart(2, '0')).join('');
  }
  // this func to get KEK Setup in APP
  // Which will contain value after user fill in password
  getCustomKEKFromApp() {
    return this.customKEKSource.getValue();
  }

  clearCustomKEK() {
    this.storage.removeItem('userCached');
    this.customKEKSource.next(null);
  }

  // async updateAllTableWithNewKEK(oldKEK: string, newKEK: string) {
  //   const allEncryptedTables = ['notebookInfo', 'notebookListInfo', 'Notes', 'offlineNoteBooks', 'offlineNotes'];
  //   for await (const table of allEncryptedTables) {
  //     await this.updateKEKForTable(table, oldKEK, newKEK);
  //   }
  // }

  // private async updateKEKForTable(table: string, oldKEK: string, newKEK: string) {
  //   const tx = this.indexDBService.indexDB.transaction(table, 'readwrite');
  //   const store = tx.objectStore(table);
  //   const cursorRequest = store.openCursor() as unknown as IDBRequest<IDBCursorWithValue | null>;

  //   cursorRequest.onsuccess = async (event) => {
  //     const cursor = (event.target as IDBRequest<IDBCursorWithValue>).result;
  //     if (cursor) {
  //       const { encryptedCEK, encryptedDataJSON, ...data } = cursor.value;
  //       const decryptedCEK: string = this.pgpEncryptionService.decryptData(encryptedCEK, oldKEK);
  //       const decryptedJSON: string = this.pgpEncryptionService.decryptData(encryptedDataJSON, decryptedCEK);

  //       const newEncryptedCEK: string = this.pgpEncryptionService.encryptData(decryptedCEK, newKEK);
  //       const newEncryptedDataJSON: string = this.pgpEncryptionService.encryptData(decryptedJSON, newEncryptedCEK);

  //       const updateRequest = await cursor.update({ ...data, encryptedCEK: newEncryptedCEK, encryptedDataJSON: newEncryptedDataJSON });
  //       updateRequest.onsuccess = function () {
  //         cursor.continue();
  //       };
  //       updateRequest.onerror = function (event) {
  //         console.error('Error updating data:', event);
  //       };
  //     } else {
  //       // Complete the transaction
  //       console.log('All data updated successfully.');
  //     }
  //   };

  //   cursorRequest.onerror = (event) => {
  //     console.error('Cursor request failed', cursorRequest.error);
  //   };
  // }

  // Legacy v1 unlock key: unsalted single-pass SHA-256. Retained ONLY to read
  // pre-migration envelopes on their upgrade unlock — never for new writes.
  private legacyHashPassword(password: string) {
    return CryptoJS.SHA256(password).toString(CryptoJS.enc.Hex);
  }
}
